import { useState } from "react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useBrand } from "../../contexts/BrandContext";
import { PasswordField } from "../../components/PasswordField";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";

/**
 * Cambio password OBBLIGATORIO al primo accesso.
 * Raggiungibile solo se `must_change_password=true`; il PlatformLayout
 * reindirizza qui e blocca ogni altra route.
 */
export default function PlatformChangePassword() {
  const { user, refresh } = useAuth();
  const { brand } = useBrand();
  const nav = useNavigate();
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwNew.length < 12) return toast.error("Nuova password troppo corta (min 12 char).");
    if (pwNew !== pwConfirm) return toast.error("Le password non coincidono.");
    if (pwNew === pwOld) return toast.error("La nuova password deve essere diversa dall'attuale.");
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: pwOld, new_password: pwNew });
      try { await http.post("/saloncentral/me/logout-all"); } catch { /* noop */ }
      await (refresh?.() || Promise.resolve());
      toast.success("Password aggiornata. Effettua un nuovo login.");
      nav("/platform/login", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  return (
    <div data-testid="platform-change-password-page"
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-dark) 0%, #2d1526 100%)", color: "white" }}>
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-2xl border border-white/10"
        style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5" style={{ color: "var(--brand-accent)" }} />
          <span className="text-xs uppercase tracking-[0.3em]" style={{ color: "var(--brand-accent)" }}>{brand.display_name}</span>
        </div>
        <h1 className="font-serif text-2xl mb-2">Cambio password obbligatorio</h1>
        <p className="text-xs text-white/60 mb-8">
          Account: <b>{user?.email}</b>. Devi impostare una nuova password prima di procedere.
          Tutte le sessioni attive verranno revocate al salvataggio.
        </p>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Password attuale</span>
          <PasswordField required value={pwOld} onChange={e => setPwOld(e.target.value)}
            autoComplete="current-password" data-testid="pcp-current" iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
        </label>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Nuova password (min 12 char)</span>
          <PasswordField required value={pwNew} onChange={e => setPwNew(e.target.value)}
            autoComplete="new-password" data-testid="pcp-new" iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
        </label>
        <label className="block mb-6">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Conferma nuova password</span>
          <PasswordField required value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
            autoComplete="new-password" data-testid="pcp-confirm" iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
        </label>
        <button type="submit" disabled={busy} data-testid="pcp-submit"
          className="w-full py-3 rounded-lg disabled:opacity-50 uppercase tracking-widest text-sm"
          style={{ background: "var(--brand-primary)", color: "white" }}>
          <KeyRound className="inline w-4 h-4 mr-2" />
          {busy ? "Aggiornamento…" : "Aggiorna password"}
        </button>
      </form>
    </div>
  );
}
